import type {CSSProperties} from 'react';
import {wedgeExactRole} from './wedgeRoles.js';

export type WedgeBriefPeopleLook = 'briefpeople-chip' | 'briefpeople-score' | 'briefpeople-score-still';

export const isWedgeBriefPeopleLook = (look: string | undefined): look is WedgeBriefPeopleLook => look !== undefined && look.startsWith('briefpeople-');

/* Released People and Signal scores controls of the Brief (Wedge). Each look keeps the declaration order of the source so the shipped ink
   rules keep matching the serialized text, and the roles keep the legacy variable names. The caller owns the labels, the glyphs and the handlers.
   Chip is the pill that sits under a decision maker card (Invite, beside the Call and Email links): an inline flex pill with a 6px gap, a 36px
   floor, 7px by 12px padding, a 1px separator edge, the alt surface fill and 12.5px label ink at 600 weight, with no underline.
   Score is the full width row of a signal score (label, bar, value, chevron) with an 11px gap and 11px by 10px padding on the page font, no fill and
   no edge; score-still is the same row without the pointer, for a score with no evidence to open.
   No look draws a transparent target and no anchor (`wedgeSettingsDrawsHit`, and a `content: none` rule naming the prefix): the legacy
   controls drew none, and the pill is 36px tall in a row of pills while the score row is wider than any target. */
export function wedgeBriefPeopleStyle(look: WedgeBriefPeopleLook): CSSProperties {
  const r = wedgeExactRole;
  if (look === 'briefpeople-chip') return {
    display: 'inline-flex', alignItems: 'center', gap: 6, minHeight: 36, padding: '7px 12px', borderRadius: 999, border: `1px solid ${r('separator')}`,
    background: r('surfaceAlt'), color: r('label'), fontSize: 12.5, fontWeight: 600, textDecoration: 'none',
  };
  return {
    width: '100%', background: 'none', border: 0, cursor: look === 'briefpeople-score' ? 'pointer' : 'default', padding: '11px 10px',
    display: 'flex', alignItems: 'center', gap: 11, fontFamily: r('font'),
  };
}
